import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, TrendingDown, TrendingUp, ArrowUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const STAGES = [
    { label: "Creator Partnerships", width: "100%" },
    { label: "Qualified Traffic", width: "76%" },
    { label: "Trial Signups", width: "52%" },
    { label: "Paying Customers", width: "32%" },
];

const OUTCOMES = [
    { icon: TrendingDown, label: "Lower blended CAC" },
    { icon: ArrowUpRight, label: "Higher conversion rates" },
    { icon: TrendingUp, label: "Better return on marketing spend" },
];

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const onSeeHow = () => {
        if (location.pathname === "/") scrollTo("how-we-do-it");
        else navigate("/#how-we-do-it");
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-white"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] gap-14 lg:gap-16 items-center">
                    {/* Left: copy */}
                    <div className="max-w-2xl">
                        <motion.h1
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            data-testid="hero-heading"
                            className="font-display font-extrabold text-[32px] leading-[1.15] sm:text-[44px] sm:leading-[1.15] md:text-[48px] md:leading-[1.15] lg:text-[54px] lg:leading-[1.15] tracking-[-0.025em] text-[#0A0A0A] text-balance"
                        >
                            Build a creator-led acquisition channel that drives trials and paying customers.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            data-testid="hero-subheading"
                            className="mt-7 max-w-xl text-[17px] md:text-lg text-gray-600 leading-[1.75] text-pretty"
                        >
                            We help you find the right creators, get better deals, and run campaigns that bring in real users. No BS metrics like views, engagement, or EMV.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <button
                                data-testid="hero-cta-primary"
                                onClick={() => scrollTo("final-cta")}
                                className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] hover:bg-black text-white h-12 px-6 text-sm font-semibold shadow-[0_16px_36px_-14px_rgba(0,0,0,0.35)] transition-all hover:-translate-y-[1px]"
                            >
                                Book a Strategy Call
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <button
                                data-testid="hero-cta-secondary"
                                onClick={onSeeHow}
                                className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#F5F5F5] text-[#0A0A0A] h-12 px-6 text-sm font-semibold border border-black/10 transition-all"
                            >
                                See How It Works
                            </button>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="mt-5 text-xs text-gray-500"
                        >
                            Free · 30 minutes · No sales pitch
                        </motion.p>
                    </div>

                    {/* Right: creator-led acquisition system */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        data-testid="hero-engine-visual"
                        className="relative"
                    >
                        <div className="relative rounded-3xl border border-black/[0.08] bg-[#FAFAFA] p-6 md:p-7">
                            <div className="flex items-center justify-between gap-3 mb-5">
                                <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-gray-500">
                                    Creator-led acquisition system
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 whitespace-nowrap">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#C6F432]" />
                                    Test · Learn · Scale
                                </span>
                            </div>

                            {/* Funnel */}
                            <p className="text-[10.5px] font-bold tracking-[0.22em] uppercase text-gray-400 mb-2.5">
                                Acquisition flow
                            </p>
                            <div data-testid="hero-funnel" className="flex flex-col">
                                {STAGES.map((s, i) => (
                                    <div key={s.label}>
                                        <div
                                            data-testid={`hero-funnel-stage-${i}`}
                                            className="rounded-xl bg-white border border-black/[0.08] px-3.5 py-2.5 flex items-center gap-3"
                                        >
                                            <span
                                                className={`flex-none h-6 w-6 rounded-full text-[10.5px] font-bold flex items-center justify-center ${
                                                    i === STAGES.length - 1
                                                        ? "bg-[#0A0A0A] text-white"
                                                        : "bg-[#FAFAFA] border border-black/[0.08] text-gray-500"
                                                }`}
                                            >
                                                {i + 1}
                                            </span>
                                            <span className="text-[12.5px] md:text-[13px] font-semibold text-[#0A0A0A] whitespace-nowrap">
                                                {s.label}
                                            </span>
                                            <span className="flex-1 h-1.5 rounded-full bg-black/[0.05] overflow-hidden">
                                                <motion.span
                                                    initial={{ width: 0 }}
                                                    animate={{ width: s.width }}
                                                    transition={{ duration: 0.9, delay: 0.45 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                                                    className={`block h-full rounded-full ${
                                                        i === STAGES.length - 1 ? "bg-[#0A0A0A]" : "bg-gray-300"
                                                    }`}
                                                />
                                            </span>
                                        </div>
                                        {i < STAGES.length - 1 && (
                                            <div className="h-5 flex items-center justify-center">
                                                <ArrowDown className="h-3 w-3 text-gray-400" strokeWidth={2.2} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Growth curve + outcomes */}
                            <div className="mt-5 rounded-xl bg-white border border-black/[0.08] p-4">
                                <div className="flex items-center justify-between gap-4">
                                    <p className="text-[10.5px] font-bold tracking-[0.22em] uppercase text-gray-400">
                                        Optimized for
                                    </p>
                                    <GrowthCurve />
                                </div>
                                <div data-testid="hero-outcomes" className="mt-3 flex flex-wrap gap-1.5">
                                    {OUTCOMES.map((o) => (
                                        <OutcomeChip key={o.label} icon={o.icon} label={o.label} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;

const OutcomeChip = ({ icon: Icon, label }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FAFAFA] border border-black/[0.06] px-2.5 h-7 text-[11.5px] font-semibold text-[#0A0A0A]">
        <Icon className="h-3 w-3" strokeWidth={2.2} />
        {label}
    </span>
);

const GrowthCurve = () => (
    <svg
        viewBox="0 0 120 28"
        className="h-6 w-[120px] flex-none"
        fill="none"
        aria-hidden="true"
    >
        <path d="M2 24 C 20 24, 28 21, 40 19 S 62 15, 76 10 S 104 3, 118 2" stroke="#D4D4D4" strokeWidth="1.5" strokeLinecap="round" />
        <motion.path
            d="M2 24 C 20 24, 28 21, 40 19 S 62 15, 76 10 S 104 3, 118 2"
            stroke="#0A0A0A"
            strokeWidth="1.5"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.4, delay: 0.8, ease: "easeOut" }}
        />
        <circle cx="40" cy="19" r="2" fill="#0A0A0A" />
        <circle cx="76" cy="10" r="2" fill="#0A0A0A" />
        <circle cx="118" cy="2" r="2.5" fill="#C6F432" stroke="#0A0A0A" strokeWidth="1" />
    </svg>
);
